import React from 'react';
import styled, { css } from 'styled-components';
import { useTable, useSortBy, useFlexLayout } from 'react-table';
import { TableColumnDefinitions } from '~/components/organism/Table/types';
import { InvoiceListData } from '../util/composeInvoiceListData';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import Catalog from '~/Catalog';
import TEST_ID from './InvoiceListTable.testid';
import Loading from '~/components/atom/Loading';
import NoResultSection from '~/components/page/NoResultSection';
import Row from '~/components/organism/Table/components/Row';
import HeaderCell from '~/components/organism/Table/components/Header/components/HeaderCell';
import RowCell from '~/components/organism/Table/components/Row/components/RowCell';
import BodyOuter from '~/components/organism/Table/components/Body/components/Outer';
import BodyInner from '~/components/organism/Table/components/Body/components/Inner';
import TableContainerStandard from '~/components/organism/Table/components/DataTableContainer/components/TableContainerStandard';
import HeaderStandard from '~/components/organism/Table/components/Header/components/Standard';
import TableContainerOuter from '~/components/organism/Table/components/DataTableContainer/components/TableContainerOuter';

const text = {
  noSearchResults: Catalog.noResults,
};
type Props = {
  columns: TableColumnDefinitions;
  data: Array<InvoiceListData>;
  networkStatus: number;
  loading: boolean;
};

const InvoiceListTable: React.FCC<Props> = ({
  columns,
  data,
  networkStatus,
  loading,
  dataTestId,
  ...rest
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn,
        disableMultiSort: true,
        autoResetSortBy: false,
        autoResetHiddenColumns: false,
        getRowId: row => row.id,
        manualSortBy: true,
        initialState: {
          sortBy: [{ id: 'createdDate', desc: true }],
        },
      },
      useFlexLayout,
      useSortBy,
    );

  let bodyComponent;
  if (isInitialLoadStatus(networkStatus) || loading) {
    bodyComponent = <Loading />;
  } else if (data.length === 0) {
    bodyComponent = (
      <StyledNoResultSection>{text.noSearchResults}</StyledNoResultSection>
    );
  } else {
    bodyComponent = (
      <BodyInner {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);

          const rowProps = { ...row.getRowProps() };
          const invoiceId = row.id;
          const isDeleted = row.original && row.original.isDeleted === true;

          return (
            <Row
              {...rowProps}
              isSelected={row.isSelected}
              isDeleted={isDeleted}
              key={rowProps.key}
              dataTestId={TEST_ID.LIST_ITEM}
              data-objectid={invoiceId}
              data-deleted={isDeleted}
            >
              {row.cells.map((cell, idx) => (
                <RowCell key={`cell-${idx}`} cell={cell} />
              ))}
            </Row>
          );
        })}
      </BodyInner>
    );
  }

  return (
    <TableContainer data-testid={dataTestId}>
      <TableContainerStandard
        {...rest}
        {...getTableProps()}
        data-testid={TEST_ID.CONTAINER}
      >
        <div>
          {headerGroups.map(headerGroup => {
            const headerGroupProps = {
              ...headerGroup.getHeaderGroupProps(),
            };
            return (
              <HeaderStandard key={headerGroupProps.key}>
                {headerGroup.headers.map((column, idx) => {
                  const isLastColumn = idx === headerGroup.headers.length - 1;
                  const headerCellKey = `header-${idx}`;

                  return (
                    <StyledHeaderCell
                      key={headerCellKey}
                      column={column}
                      isLastColumn={isLastColumn}
                    />
                  );
                })}
              </HeaderStandard>
            );
          })}
        </div>
        <BodyOuter>{bodyComponent}</BodyOuter>
      </TableContainerStandard>
    </TableContainer>
  );
};

const TableContainer = styled(TableContainerOuter)(
  ({ theme }) => css`
    padding: ${theme.space('s')};
  `,
);

const StyledHeaderCell = styled(HeaderCell)<{}>(
  ({ theme }) => css`
    padding-bottom: ${theme.space('l')};
  `,
);

const defaultColumn = {
  minWidth: 20,
  width: 120,
  maxWidth: 200,
};

const StyledNoResultSection = styled(NoResultSection)<{}>`
  max-width: 800px;
`;

export default InvoiceListTable;
